import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import AlertToast from '../../../components/common/AlertToast';
import careerRoadmapBanner from '../../../assets/learning/career-roadmap-banner.png';
import './StudentJobOpportunitiesPage.css';

const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5030/api').replace(/\/api\/?$/, '');

const withAuthToken = (url) => {
  if (!url) return '';
  const input = String(url || '').trim();
  if (!input || /^(data:|blob:)/i.test(input)) return input;
  const raw = /^https?:/i.test(input) ? input : `${apiBase}${input.startsWith('/') ? '' : '/'}${input}`;
  const token = localStorage.getItem('token');
  if (!token) return raw;
  if (/[?&]t=/.test(raw)) return raw;
  return raw.includes('?') ? `${raw}&t=${encodeURIComponent(token)}` : `${raw}?t=${encodeURIComponent(token)}`;
};

const fmtDate = (value) => (value ? new Date(value).toLocaleDateString('en-IN') : 'Open');
const normalizeTags = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item?.value || item || '').trim()).filter(Boolean);
  return String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
};
const skillIcon = (value) => {
  const skill = String(value || '').toLowerCase();
  if (skill.includes('react') || skill.includes('javascript') || skill.includes('node')) return 'bi-code-slash';
  if (skill.includes('python') || skill.includes('data') || skill.includes('sql')) return 'bi-bar-chart-line';
  if (skill.includes('design') || skill.includes('ui') || skill.includes('ux')) return 'bi-palette';
  if (skill.includes('cloud') || skill.includes('devops')) return 'bi-cloud-check';
  return 'bi-lightning-charge';
};
const formatLabel = (value, fallback = '') => String(value || fallback).replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
const daysUntil = (value) => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return null;
  return Math.ceil((timestamp - Date.now()) / 86400000);
};
const fmtSalaryRange = (min, max) => {
  const minValue = Number(min || 0);
  const maxValue = Number(max || 0);
  if (!minValue && !maxValue) return '';
  if (minValue && maxValue) return `Rs ${minValue.toLocaleString('en-IN')} - Rs ${maxValue.toLocaleString('en-IN')}`;
  if (minValue) return `From Rs ${minValue.toLocaleString('en-IN')}`;
  return `Up to Rs ${maxValue.toLocaleString('en-IN')}`;
};

const DEMO_CAREER_VIDEOS = [
  {
    _id: 'demo-career-video-1',
    promoTitle: 'Build Your Career Story',
    promoText: 'Career inspiration',
    imageUrl: careerRoadmapBanner,
    promoVideoUrl: 'https://www.youtube.com/watch?v=UF8uR6Z6KLc',
    isDemo: true,
  },
  {
    _id: 'demo-career-video-2',
    promoTitle: 'Turn Goals Into Action',
    promoText: 'Career mindset',
    imageUrl: careerRoadmapBanner,
    promoVideoUrl: 'https://www.youtube.com/watch?v=arj7oStGLkU',
    isDemo: true,
  },
  {
    _id: 'demo-career-video-3',
    promoTitle: 'Grow With Grit',
    promoText: 'Skills and persistence',
    imageUrl: careerRoadmapBanner,
    promoVideoUrl: 'https://www.youtube.com/watch?v=H14bBuluwB8',
    isDemo: true,
  },
];

export default function StudentJobOpportunitiesPage() {
  const navigate = useNavigate();
  const storyCarouselRef = useRef(null);
  const [jobs, setJobs] = useState([]);
  const [resumeProfile, setResumeProfile] = useState(null);
  const [successStories, setSuccessStories] = useState([]);
  const [applications, setApplications] = useState([]);
  const [showApplications, setShowApplications] = useState(false);
  const [expandedJobDescriptions, setExpandedJobDescriptions] = useState({});
  const [skillEditorOpen, setSkillEditorOpen] = useState(false);
  const [skillDraft, setSkillDraft] = useState('');
  const [savingSkills, setSavingSkills] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({ search: '', jobType: '', workMode: '', minSalary: '', maxSalary: '' });
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const response = await api.get('/training/resumes/jobs');
        const records = response.data?.records || response.data?.data || [];
        if (mounted) setJobs(Array.isArray(records) ? records : []);
      } catch (err) {
        if (mounted) setAlert({ show: true, message: err.response?.data?.message || 'Failed to load job opportunities.', type: 'danger' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get('/training/resumes/builder/my').catch(() => null),
      api.get('/training/promotions').catch(() => null),
      api.get('/training/resumes/job-applications/my').catch(() => null),
    ]).then(([profileResponse, promotionResponse, applicationResponse]) => {
      if (!mounted) return;
      const profile = profileResponse?.data?.data || profileResponse?.data || null;
      const promotions = promotionResponse?.data?.records || promotionResponse?.data?.data || [];
      const applicationRecords = applicationResponse?.data?.records || applicationResponse?.data?.data || [];
      setResumeProfile(profile && typeof profile === 'object' ? profile : null);
      setSuccessStories((Array.isArray(promotions) ? promotions : [])
        .filter((item) => item?.isActive !== false && (
          ['video_review', 'certificate_showcase'].includes(String(item?.type || ''))
          || (String(item?.type || '') === 'promotion' && (item?.promoVideoUrl || item?.videoUrl))
        ))
        .slice(0, 12));
      setApplications(Array.isArray(applicationRecords) ? applicationRecords : []);
    });
    return () => { mounted = false; };
  }, []);

  const filteredJobs = useMemo(() => {
    const search = String(filters.search || '').trim().toLowerCase();
    return (jobs || []).filter((job) => {
      if (job?.isActive === false) return false;
      if (filters.jobType && String(job.jobType || '') !== String(filters.jobType)) return false;
      if (filters.workMode && String(job.workMode || '') !== String(filters.workMode)) return false;
      if (filters.minSalary && Number(job.maxSalary || 0) < Number(filters.minSalary || 0)) return false;
      if (filters.maxSalary && Number(job.minSalary || 0) > Number(filters.maxSalary || 0)) return false;
      if (!search) return true;
      return [job.title, job.companyName, job.location, ...normalizeTags(job.requiredSkills)]
        .some((value) => String(value || '').toLowerCase().includes(search));
    });
  }, [filters, jobs]);

  const activeJobs = useMemo(() => (jobs || []).filter((job) => job?.isActive !== false), [jobs]);
  const jobInsights = useMemo(() => {
    const flexibleCount = activeJobs.filter((job) => ['remote', 'hybrid'].includes(String(job.workMode || '').toLowerCase())).length;
    const internshipCount = activeJobs.filter((job) => String(job.jobType || '').toLowerCase() === 'internship').length;
    const closingSoonCount = activeJobs.filter((job) => {
      const remaining = daysUntil(job.applicationDeadline);
      return remaining != null && remaining >= 0 && remaining <= 14;
    }).length;
    return { flexibleCount, internshipCount, closingSoonCount };
  }, [activeJobs]);

  const hiringPulse = useMemo(() => {
    const companies = new Set();
    const skillCounts = new Map();
    const salaryMidpoints = [];

    activeJobs.forEach((job) => {
      const company = String(job.companyName || '').trim();
      if (company) companies.add(company.toLowerCase());
      normalizeTags(job.requiredSkills).forEach((skill) => {
        const key = skill.toLowerCase();
        const current = skillCounts.get(key) || { label: skill, count: 0 };
        skillCounts.set(key, { ...current, count: current.count + 1 });
      });
      const min = Number(job.minSalary || 0);
      const max = Number(job.maxSalary || 0);
      if (min || max) salaryMidpoints.push(min && max ? (min + max) / 2 : min || max);
    });

    const averageSalary = salaryMidpoints.length
      ? Math.round(salaryMidpoints.reduce((total, value) => total + value, 0) / salaryMidpoints.length)
      : 0;
    const topSkills = [...skillCounts.values()].sort((a, b) => b.count - a.count).slice(0, 5);
    return { companyCount: companies.size, averageSalary, topSkills };
  }, [activeJobs]);

  const studentSkillMatches = useMemo(() => {
    const profileSkills = [
      ...normalizeTags(resumeProfile?.technicalSkills),
      ...normalizeTags(resumeProfile?.autoFillMeta?.sourceSkills),
    ];
    const uniqueSkills = [...new Map(profileSkills.map((skill) => [skill.toLowerCase(), skill])).values()];
    return uniqueSkills.map((skill) => ({
      skill,
      jobs: activeJobs.filter((job) => normalizeTags(job.requiredSkills)
        .some((requiredSkill) => requiredSkill.toLowerCase().includes(skill.toLowerCase()) || skill.toLowerCase().includes(requiredSkill.toLowerCase()))).length,
    })).sort((a, b) => b.jobs - a.jobs).slice(0, 5);
  }, [activeJobs, resumeProfile]);

  const careerChallengeProgress = useMemo(() => {
    if (!resumeProfile) return 0;
    const checkpoints = [
      Boolean(resumeProfile?.contact?.fullName && resumeProfile?.contact?.email),
      normalizeTags(resumeProfile?.technicalSkills).length > 0,
      Array.isArray(resumeProfile?.projects) && resumeProfile.projects.length > 0,
      String(resumeProfile?.status || '').toLowerCase() === 'ready',
    ];
    return Math.round((checkpoints.filter(Boolean).length / checkpoints.length) * 100);
  }, [resumeProfile]);
  const hasPublishedCareerVideo = successStories.some((story) => story?.videoUrl || story?.promoVideoUrl);
  const placementStories = hasPublishedCareerVideo
    ? successStories
    : [...successStories, ...DEMO_CAREER_VIDEOS].slice(0, 12);

  const hasFilters = Object.values(filters).some((value) => String(value || '').trim());
  const activeFilterCount = ['jobType', 'workMode', 'minSalary', 'maxSalary']
    .filter((key) => String(filters[key] || '').trim()).length;
  const clearFilters = () => {
    setFilters({ search: '', jobType: '', workMode: '', minSalary: '', maxSalary: '' });
    setShowAdvancedFilters(false);
  };
  const setQuickFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      jobType: key === 'jobType' ? value : '',
      workMode: key === 'workMode' ? value : '',
      minSalary: key ? prev.minSalary : '',
      maxSalary: key ? prev.maxSalary : '',
    }));
  };

  const saveSkills = async (skills) => {
    setSavingSkills(true);
    try {
      const response = await api.put('/training/resumes/builder/my', {
        ...(resumeProfile || {}),
        technicalSkills: skills.map((value) => ({ value })),
      });
      const updatedProfile = response.data?.data || response.data || null;
      if (updatedProfile) setResumeProfile(updatedProfile);
      setAlert({ show: true, message: 'Skills updated.', type: 'success' });
    } catch (err) {
      setAlert({ show: true, message: err.response?.data?.message || 'Could not update skills.', type: 'danger' });
    } finally {
      setSavingSkills(false);
    }
  };

  const addSkill = () => {
    const value = String(skillDraft || '').trim();
    const current = normalizeTags(resumeProfile?.technicalSkills);
    if (!value || current.some((skill) => skill.toLowerCase() === value.toLowerCase())) return;
    setSkillDraft('');
    saveSkills([...current, value]);
  };

  const removeSkill = (skillToRemove) => {
    saveSkills(normalizeTags(resumeProfile?.technicalSkills).filter((skill) => skill !== skillToRemove));
  };

  const trackApplication = async (job, applicationMethod) => {
    if (!job?._id) return;
    try {
      const response = await api.post(`/training/resumes/jobs/${job._id}/applications`, { applicationMethod });
      const record = response.data?.data || response.data || {};
      setApplications((current) => [
        { ...record, jobId: job, appliedAt: record.appliedAt || new Date().toISOString() },
        ...current.filter((item) => String(item?.jobId?._id || item?.jobId) !== String(job._id)),
      ]);
    } catch (_) {
      // The external application must remain available even if tracking is temporarily unavailable.
    }
  };

  const applyByEmail = (job) => {
    const email = String(job?.applyEmail || '').trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setAlert({ show: true, message: 'A valid application email is not available for this job.', type: 'danger' });
      return;
    }
    trackApplication(job, 'email');
    const title = String(job?.title || 'the advertised position').trim();
    const company = String(job?.companyName || '').trim();
    const subject = `Application for ${title}`;
    const body = [
      `Hello${job?.contactPerson ? ` ${job.contactPerson}` : ''},`,
      '',
      `I am writing to apply for ${title}${company ? ` at ${company}` : ''}.`,
      'Please find my application details and resume attached.',
      '',
      'Thank you.',
    ].join('\n');
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    const mailtoUrl = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    const composer = window.open(gmailUrl, '_blank');
    if (composer) {
      composer.opener = null;
      return;
    }
    window.location.href = mailtoUrl;
  };

  return (
    <div className="container-fluid py-3 student-jobs-page">
      <AlertToast message={alert.message} type={alert.type} show={alert.show} onClose={() => setAlert((prev) => ({ ...prev, show: false }))} />
      <section className="student-job-search-panel">
        <div className="student-job-search-heading">
          <div><h2>Find your best match</h2><p>{filteredJobs.length} role{filteredJobs.length === 1 ? '' : 's'} currently match your search</p></div>
          {hasFilters ? <button type="button" className="btn btn-link btn-sm text-decoration-none" onClick={clearFilters}>Clear filters</button> : null}
        </div>
        <div className="student-job-filter-primary">
          <label className="student-job-search-box">
            <i className="bi bi-search" />
            <input value={filters.search} onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))} placeholder="Search jobs, companies or skills" />
          </label>
          <button type="button" className={`student-job-filter-toggle ${showAdvancedFilters ? 'is-open' : ''}`} onClick={() => setShowAdvancedFilters((value) => !value)} aria-expanded={showAdvancedFilters}>
            <i className="bi bi-sliders" /> Filters {activeFilterCount ? <span>{activeFilterCount}</span> : null}
          </button>
          <button type="button" className="student-job-find-button" onClick={() => document.getElementById('student-job-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
            Find Jobs <i className="bi bi-arrow-down" />
          </button>
        </div>
        <div className="student-job-quick-filters" aria-label="Quick filters">
          <button type="button" className={!filters.jobType && !filters.workMode ? 'active' : ''} onClick={() => setQuickFilter('', '')}>All</button>
          <button type="button" className={filters.jobType === 'full_time' ? 'active' : ''} onClick={() => setQuickFilter('jobType', 'full_time')}>Full time</button>
          <button type="button" className={filters.jobType === 'internship' ? 'active' : ''} onClick={() => setQuickFilter('jobType', 'internship')}>Internships</button>
          <button type="button" className={filters.workMode === 'remote' ? 'active' : ''} onClick={() => setQuickFilter('workMode', 'remote')}>Remote</button>
          <button type="button" className={filters.workMode === 'hybrid' ? 'active' : ''} onClick={() => setQuickFilter('workMode', 'hybrid')}>Hybrid</button>
        </div>
        <div className={`student-job-advanced-filters ${showAdvancedFilters ? 'is-open' : ''}`}>
          <select aria-label="Job type" value={filters.jobType} onChange={(e) => setFilters((prev) => ({ ...prev, jobType: e.target.value }))}>
            <option value="">All job types</option><option value="full_time">Full Time</option><option value="part_time">Part Time</option><option value="internship">Internship</option><option value="contract">Contract</option><option value="freelance">Freelance</option>
          </select>
          <select aria-label="Work mode" value={filters.workMode} onChange={(e) => setFilters((prev) => ({ ...prev, workMode: e.target.value }))}>
            <option value="">All work modes</option><option value="onsite">Onsite</option><option value="remote">Remote</option><option value="hybrid">Hybrid</option>
          </select>
          <input aria-label="Minimum salary" type="number" value={filters.minSalary} onChange={(e) => setFilters((prev) => ({ ...prev, minSalary: e.target.value }))} placeholder="Min salary" />
          <input aria-label="Maximum salary" type="number" value={filters.maxSalary} onChange={(e) => setFilters((prev) => ({ ...prev, maxSalary: e.target.value }))} placeholder="Max salary" />
        </div>
      </section>

      <section className="student-job-results" id="student-job-results">
        <div className="student-job-results-heading">
          <div>
            <span>Recommended openings</span>
            <h2>Opportunities Waiting for You</h2>
            <p>Explore jobs that match your skills and take your next career step.</p>
          </div>
          <strong>{filteredJobs.length} result{filteredJobs.length === 1 ? '' : 's'}</strong>
        </div>
        <div className="student-jobs-content-grid">
          <main>
            {loading ? <div className="student-jobs-loading"><div className="spinner-border text-primary" /><span>Finding opportunities...</span></div> : (
              <div className="student-jobs-list">
                {filteredJobs.length ? filteredJobs.map((job) => {
                  const skills = normalizeTags(job.requiredSkills);
                  const remainingDays = daysUntil(job.applicationDeadline);
                  const deadlineTone = remainingDays != null && remainingDays >= 0 && remainingDays <= 7 ? 'urgent' : '';
                  const description = job.description || 'Review the role details and apply through the provided application channel.';
                  const descriptionExpanded = Boolean(expandedJobDescriptions[String(job._id)]);
                  const canExpandDescription = String(description).trim().length > 110;
                  return (
                    <article className="student-job-card" key={job._id}>
                      <div className="student-job-card-topline">
                        <div className="student-job-card-media">
                          {job.imageUrl ? <img src={withAuthToken(job.imageUrl)} alt={job.companyName || job.title} /> : <div className="student-job-image-fallback"><i className="bi bi-building" /></div>}
                        </div>
                        <div className="student-job-card-title-row">
                          <div>
                            <span className="student-job-type-badge">{formatLabel(job.jobType, 'Opportunity')}</span>
                            <h3>{job.title || 'Open position'}</h3>
                            <p>{job.companyName || 'Company'}</p>
                          </div>
                          <span className="student-job-active-dot">Active</span>
                        </div>
                      </div>
                      <div className="student-job-card-body">
                        <div className="student-job-facts">
                          {job.location ? <span><i className="bi bi-geo-alt" />{job.location}</span> : null}
                          <span><i className="bi bi-laptop" />{formatLabel(job.workMode, 'Onsite')}</span>
                          <span><i className="bi bi-clock-history" />{formatLabel(job.jobType, 'Opportunity')}</span>
                          {fmtSalaryRange(job.minSalary, job.maxSalary) ? <span><i className="bi bi-cash-stack" />{fmtSalaryRange(job.minSalary, job.maxSalary)}</span> : null}
                        </div>
                        <div className="student-job-description-wrap">
                          <p
                            id={`student-job-description-${job._id}`}
                            className={`student-job-description ${descriptionExpanded ? 'is-expanded' : ''}`}
                          >
                            {description}
                          </p>
                          {canExpandDescription ? (
                            <button
                              type="button"
                              className="student-job-description-toggle"
                              aria-expanded={descriptionExpanded}
                              aria-controls={`student-job-description-${job._id}`}
                              onClick={() => setExpandedJobDescriptions((current) => ({
                                ...current,
                                [String(job._id)]: !current[String(job._id)],
                              }))}
                            >
                              {descriptionExpanded ? 'Show less' : 'Read more'}
                              <i className={`bi ${descriptionExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                            </button>
                          ) : null}
                        </div>
                        {skills.length ? <div className="student-job-skills">{skills.slice(0, 5).map((skill) => <span key={`${job._id}-${skill}`}>{skill}</span>)}{skills.length > 5 ? <span>+{skills.length - 5}</span> : null}</div> : null}
                        <div className="student-job-card-footer">
                          <div className={`student-job-deadline ${deadlineTone}`}><i className="bi bi-calendar-event" /><span><small>Apply before</small>{fmtDate(job.applicationDeadline)}</span></div>
                          {job.applyUrl ? <a className="btn btn-primary" href={job.applyUrl} target="_blank" rel="noreferrer" onClick={() => trackApplication(job, 'url')}>Apply Now <i className="bi bi-arrow-up-right" /></a> : null}
                          {!job.applyUrl && job.applyEmail ? <button type="button" className="btn btn-primary" onClick={() => applyByEmail(job)}>Apply Now <i className="bi bi-send" /></button> : null}
                          {!job.applyUrl && !job.applyEmail ? <button type="button" className="btn btn-primary" onClick={() => setAlert({ show: true, message: 'Application details are not available for this job yet.', type: 'warning' })}>Apply Now <i className="bi bi-arrow-right" /></button> : null}
                        </div>
                      </div>
                    </article>
                  );
                }) : <div className="student-jobs-empty"><i className="bi bi-search" /><h3>No matching opportunities</h3><p>Try changing your search or clearing the filters.</p>{hasFilters ? <button type="button" className="btn btn-outline-primary" onClick={clearFilters}>Clear filters</button> : null}</div>}
              </div>
            )}
          </main>

        </div>
      </section>

      <section className="student-job-visual-section">
        <div className="student-job-visual-heading">
          <div><span>Skill match</span><h2>Jobs Matching Your Skills</h2></div>
          <button type="button" onClick={() => setSkillEditorOpen((open) => !open)}>{skillEditorOpen ? 'Close' : 'Edit skills'} <i className={`bi ${skillEditorOpen ? 'bi-x-lg' : 'bi-pencil'}`} /></button>
        </div>
        {skillEditorOpen ? (
          <div className="student-job-skill-editor">
            <div className="student-job-skill-editor-input">
              <input value={skillDraft} onChange={(event) => setSkillDraft(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addSkill(); } }} placeholder="Add a skill" disabled={savingSkills} />
              <button type="button" onClick={addSkill} disabled={savingSkills || !skillDraft.trim()}><i className="bi bi-plus-lg" /> Add</button>
            </div>
            <div className="student-job-skill-editor-tags">
              {normalizeTags(resumeProfile?.technicalSkills).map((skill) => <span key={skill}><span className="student-job-saved-skill-icon"><i className={`bi ${skillIcon(skill)}`} /><small>{skill.slice(0, 2).toUpperCase()}</small></span>{skill}<button type="button" aria-label={`Remove ${skill}`} onClick={() => removeSkill(skill)} disabled={savingSkills}><i className="bi bi-x" /></button></span>)}
              {!normalizeTags(resumeProfile?.technicalSkills).length ? <small>Add skills to improve your job matches.</small> : null}
            </div>
          </div>
        ) : null}
        {studentSkillMatches.length ? (
          <div className="student-job-skill-match-grid">
            {studentSkillMatches.map((match, index) => (
              <button type="button" className={`student-job-skill-match tone-${(index % 5) + 1}`} key={match.skill} onClick={() => {
                setFilters((prev) => ({ ...prev, search: match.skill }));
                document.getElementById('student-job-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}>
                <span className="student-job-skill-icon"><i className={`bi ${skillIcon(match.skill)}`} /><small>{match.skill.slice(0, 2).toUpperCase()}</small></span>
                <strong>{match.skill}</strong>
                <i className="bi bi-arrow-right" />
                <span className="student-job-count-badge">{match.jobs} job{match.jobs === 1 ? '' : 's'}</span>
              </button>
            ))}
          </div>
        ) : (
          <button type="button" className="student-job-skills-empty" onClick={() => navigate('/resumes')}>
            <i className="bi bi-person-badge" /><span><strong>Add your skills</strong><small>Get matched with relevant jobs</small></span><i className="bi bi-arrow-right" />
          </button>
        )}
      </section>

      <section className="student-job-visual-section">
        <div className="student-job-visual-heading">
          <div><span>Student success</span><h2>Placement Stories</h2></div>
          {placementStories.length > 1 ? <div className="student-job-carousel-controls"><button type="button" aria-label="Previous story" onClick={() => storyCarouselRef.current?.scrollBy({ left: -280, behavior: 'smooth' })}><i className="bi bi-chevron-left" /></button><button type="button" aria-label="Next story" onClick={() => storyCarouselRef.current?.scrollBy({ left: 280, behavior: 'smooth' })}><i className="bi bi-chevron-right" /></button></div> : null}
        </div>
        {placementStories.length ? (
          <div className="student-job-success-track" ref={storyCarouselRef}>
            {placementStories.map((story) => {
              const image = story.thumbnailUrl || story.imageUrl || story.certificateImageUrl || '';
              const videoUrl = story.videoUrl || story.promoVideoUrl || '';
              const name = story.reviewerName || story.studentName || story.promoTitle || 'Top Skilled learner';
              const highlight = story.achievement || story.reviewerCourse || story.courseName || story.promoText || 'Career milestone achieved';
              return (
                <button type="button" className={`student-job-success-card ${videoUrl ? 'has-video' : ''}`} key={story._id} onClick={() => { if (videoUrl) window.open(videoUrl, '_blank', 'noopener,noreferrer'); }}>
                  <div className="student-job-success-photo">
                    {image ? <img src={image === careerRoadmapBanner || /^(https?:|data:|blob:)/i.test(image) ? image : withAuthToken(image)} alt={name} onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = careerRoadmapBanner; }} /> : <img src={careerRoadmapBanner} alt="Career success story" />}
                    {videoUrl ? <i className="bi bi-play-fill" /> : null}
                  </div>
                  <div><strong>{name}</strong><span>{highlight}</span>{story.isDemo ? <small>Demo video</small> : null}</div>
                  <i className={`bi ${videoUrl ? 'bi-play-circle-fill' : 'bi-patch-check-fill'}`} aria-label={videoUrl ? 'Play career video' : 'Student success'} />
                </button>
              );
            })}
          </div>
        ) : (
          <div className="student-job-success-empty"><i className="bi bi-people" /><span>Student milestones will appear here.</span></div>
        )}
      </section>

      <div className="student-job-action-challenge-grid">
        <section className="student-job-visual-section student-job-quick-actions-section">
          <div className="student-job-visual-heading"><div><span>Career toolkit</span><h2>Quick Career Actions</h2></div></div>
          <div className="student-job-quick-actions">
            <button type="button" onClick={() => navigate('/resumes')}><i className="bi bi-file-earmark-person" /><span>Build Resume</span></button>
            <button type="button" onClick={() => document.getElementById('student-job-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}><i className="bi bi-search" /><span>Find Jobs</span></button>
            <button type="button" onClick={() => navigate('/lms?tab=for-you')}><i className="bi bi-mortarboard" /><span>Improve Skills</span></button>
            <button type="button" onClick={() => setShowApplications((show) => !show)}><i className="bi bi-kanban" /><span>Track Applications</span>{applications.length ? <small className="student-job-action-count">{applications.length}</small> : null}</button>
          </div>
        </section>

        <section className="student-job-career-challenge">
          <div className="student-job-challenge-copy">
            <span><i className="bi bi-trophy-fill" /> Career challenge</span>
            <h2>7-Day Career Challenge</h2>
            <div className="student-job-challenge-progress"><span style={{ width: `${careerChallengeProgress}%` }} /></div>
            <small>{careerChallengeProgress}% resume ready</small>
          </div>
          <button type="button" onClick={() => navigate('/resumes')}>Continue <i className="bi bi-arrow-right" /></button>
        </section>
      </div>

      {showApplications ? (
        <section className="student-job-application-tracker">
          <div className="student-job-visual-heading"><div><span>Application tracker</span><h2>Your Applications</h2></div><button type="button" onClick={() => setShowApplications(false)}>Close <i className="bi bi-x-lg" /></button></div>
          {applications.length ? <div className="student-job-application-list">{applications.map((application) => {
            const job = application.jobId || {};
            return <div key={application._id || `${job._id}-${application.appliedAt}`}><span className="student-job-application-logo">{job.imageUrl ? <img src={withAuthToken(job.imageUrl)} alt="" /> : <i className="bi bi-building" />}</span><div><strong>{job.title || 'Job application'}</strong><small>{job.companyName || 'Company'} · {fmtDate(application.appliedAt)}</small></div><span className="student-job-application-status">{formatLabel(application.status, 'Applied')}</span></div>;
          })}</div> : <div className="student-job-success-empty"><i className="bi bi-send" /><span>Your applications will appear here after you apply.</span></div>}
        </section>
      ) : null}

      <section className="student-jobs-metrics" aria-label="Opportunity overview">
        {[
          { label: 'Open roles', value: activeJobs.length, icon: 'bi-briefcase', tone: 'indigo' },
          { label: 'Remote or hybrid', value: jobInsights.flexibleCount, icon: 'bi-laptop', tone: 'cyan' },
          { label: 'Internships', value: jobInsights.internshipCount, icon: 'bi-mortarboard', tone: 'green' },
          { label: 'Closing soon', value: jobInsights.closingSoonCount, icon: 'bi-hourglass-split', tone: 'orange' },
        ].map((metric) => (
          <div className="student-job-metric" key={metric.label}>
            <span className={`student-job-metric-icon ${metric.tone}`}><i className={`bi ${metric.icon}`} /></span>
            <div><strong>{metric.value}</strong><span>{metric.label}</span></div>
          </div>
        ))}
      </section>

      <section className="student-job-hiring-pulse">
        <div className="student-job-section-heading">
          <div><span>Build Skills. Get Noticed. Get Hired.</span><h2>Your Skills Can Open New Doors</h2></div>
          <p>Live insights from the opportunities currently available to you.</p>
        </div>
        <div className="student-job-pulse-content">
          <div className="student-job-pulse-stat"><i className="bi bi-buildings" /><span><strong>{hiringPulse.companyCount}</strong> hiring companies</span></div>
          <div className="student-job-pulse-stat"><i className="bi bi-cash-coin" /><span><strong>{hiringPulse.averageSalary ? `Rs ${Math.round(hiringPulse.averageSalary / 1000)}K` : 'Open'}</strong> average listed pay</span></div>
          <div className="student-job-pulse-skills">
            <small>Skills in demand</small>
            <div>{hiringPulse.topSkills.length ? hiringPulse.topSkills.map((skill) => <span key={skill.label}>{skill.label}</span>) : <span>New skills coming soon</span>}</div>
          </div>
        </div>
      </section>

      <section className="student-job-roadmap">
        <div className="student-job-section-heading">
          <div><span>Keep Growing Towards Your Career Goals</span><h2>Turn an opening into an interview</h2></div>
          <button type="button" onClick={() => navigate('/resumes')}>Open resume builder <i className="bi bi-arrow-right" /></button>
        </div>
        <div className="student-job-roadmap-grid">
          <div><span>1</span><i className="bi bi-person-vcard" /><strong>Prepare your profile</strong><p>Lead with relevant skills, projects, and measurable results.</p></div>
          <div><span>2</span><i className="bi bi-bullseye" /><strong>Match the role</strong><p>Use the job description to tailor your resume for each application.</p></div>
          <div><span>3</span><i className="bi bi-send-check" /><strong>Apply with focus</strong><p>Prioritize strong-fit roles and apply before the deadline.</p></div>
        </div>
      </section>

      <aside className="student-job-career-hook">
        <span className="student-job-career-icon"><i className="bi bi-file-earmark-person" /></span>
        <div className="student-job-career-copy">
          <small>Before you apply</small>
          <h2>Make your first impression count.</h2>
          <p>Keep your resume current, highlight relevant skills, and tailor it for each role.</p>
        </div>
        <ul><li><i className="bi bi-check-circle-fill" /> Lead with measurable outcomes</li><li><i className="bi bi-check-circle-fill" /> Match skills from the job post</li><li><i className="bi bi-check-circle-fill" /> Keep projects easy to scan</li></ul>
        <button type="button" className="btn btn-light" onClick={() => navigate('/resumes')}>Improve my resume <i className="bi bi-arrow-right" /></button>
      </aside>
    </div>
  );
}
